/* eslint-disable react-hooks/exhaustive-deps */
import { useContext, useEffect, useState } from 'react'
import { Doughnut } from 'react-chartjs-2'
import { SubmitHandler, useForm } from 'react-hook-form'
import { Tooltip } from 'react-tooltip'

import useServer from '../../../api/useServerMain'
import CurrencyInput from '../../../components/forms/CurrencyInput'
import AlertSquare from '../../../components/misc/AlertSquare'
import Button from '../../../components/misc/Button'
import ButtonSelector from '../../../components/misc/ButtonSelector'
import GenericToggle from '../../../components/misc/GenericToggle'
import useProduct from '../../../hooks/useProduct'
import { useAppSelector } from '../../../store/hooks'
import { formatCalendar, formatCurrency } from '../../../utils/helpers'
import { DetailProductContext } from '../DetailProductContainer'

const Sales_Price = () => {
  const { product, updateProduct } = useContext(DetailProductContext)
  const { business } = useAppSelector((state) => state.init)
  const { allowRoles: verifyRoles, denyRoles } = useServer()
  const { handleSubmit, control, unregister, getValues } = useForm()
  const { getPrice, getProfit } = useProduct()

  //Prices by PriceSystems----------------------------------------------------------------------------

  const [priceSystem, setPriceSystem] = useState(false)
  const [currentPriceSystem, setCurrentPriceSystem] = useState<{
    id: number
    name: string
  } | null>(null)

  const precission = Number(
    business?.configurationsKey.find(
      (item) => item.key === 'precission_after_coma'
    )?.value
  )

  const currentPrice = product?.prices.find(
    (price) => price.priceSystemId === currentPriceSystem?.id
  )

  //----------------------------------------------------------------------------------------------------

  const profit = getProfit({ product: product! }) as number

  const onSubmit: SubmitHandler<Record<string, string | number | boolean>> = (
    data
  ) => {
    data.prices = getValues('prices')
    updateProduct && updateProduct(product?.id, data)
  }

  useEffect(() => {
    if (product && product?.prices.length > 1) {
      setPriceSystem(true)
    } else {
      setPriceSystem(false)
    }
  }, [])

  useEffect(() => {
    if (!priceSystem) {
      unregister('prices')
    }
    const mainPriceSystem = business?.priceSystems.find((item) => item.isMain)!
    setCurrentPriceSystem({
      id: mainPriceSystem?.id,
      name: mainPriceSystem.name,
    })
  }, [priceSystem])

  const roleIsAdmin = verifyRoles([
    'ADMIN',
    'MANAGER_COST_PRICES',
    'PRODUCT_PROCESATOR',
  ])

  return (
    <div className="relative grid h-[34rem] grid-cols-2 gap-2 rounded-md border border-gray-300 p-5">
      {roleIsAdmin && verifyRoles(['OWNER', 'MANAGER_COST_PRICES']) && (
        <div className="relative m-auto flex min-h-0 flex-col">
          <Doughnut
            style={{ display: 'flex', minHeight: 0 }}
            data={{
              labels: ['Costo ponderado', 'Ganancia Bruta'],
              datasets: [
                {
                  data: [product?.averageCost ?? 0, profit],
                  backgroundColor: ['rgb(255, 99, 132)', 'rgb(54, 162, 235)'],
                  datalabels: {
                    color: 'black',
                    formatter: (value) =>
                      formatCurrency(
                        value,
                        business?.costCurrency ?? 'CUP',
                        precission
                      ),
                  },
                },
              ],
            }}
            options={{
              responsive: true,
            }}
          />
          <div className="absolute left-[100px] top-[150px]">
            <h3 className="text-center text-sm font-medium">
              Precio de venta:
            </h3>
            <p className={`text-center text-sm font-semibold text-gray-700`}>
              {
                getPrice({
                  product: product!,
                  priceSystemId: currentPriceSystem?.id,
                }) as string
              }
            </p>
          </div>
          {/**GANANCIA POR PRECIO DE VENTA */}
          <div className="-mx-10 mt-2 inline-flex items-center rounded-md border p-2 shadow-md">
            <div className="flex-grow flex-col">
              <h3 className="font-medium">Ganancia por precio de ventas</h3>
              <p
                className={`text-lg font-semibold ${
                  profit > 0 ? 'text-green-600' : 'text-red-600'
                }`}
              >
                {getProfit({
                  product: product!,
                  priceSystemId: currentPriceSystem?.id,
                  mode: 'price',
                })}
              </p>
            </div>
            <div className="flex flex-shrink justify-center">
              <p
                className={`flex items-center justify-center p-2 font-semibold text-gray-100 ${
                  profit > 0 ? 'bg-green-400' : 'bg-red-400'
                } h-8 w-8 rounded-full p-8`}
              >
                {getProfit({
                  product: product!,
                  priceSystemId: currentPriceSystem?.id,
                  mode: 'percent',
                }) + '%'}
              </p>
            </div>
          </div>
          {/**TASA DE UTILIDAD SOBRE COSTOS */}
          <div
            data-tooltip-id="info"
            className="-mx-10 mt-2 inline-flex items-center rounded-md border p-2 shadow-md"
          >
            <div className="flex-grow flex-col">
              <h3 className="font-medium">Tasa de utilidad sobre costos</h3>
              <p
                className={`p-2 text-lg font-semibold ${
                  profit > 0 ? 'text-green-600' : 'text-red-600'
                }`}
              >
                {getProfit({
                  product: product!,
                  priceSystemId: currentPriceSystem?.id,
                  mode: 'price',
                })}
              </p>
            </div>
            <div className="flex flex-shrink justify-center">
              <p
                className={`flex items-center justify-center p-2 font-semibold text-gray-100 ${
                  profit > 0 ? 'bg-green-400' : 'bg-red-400'
                } h-8 w-8 rounded-full p-8`}
              >
                {getProfit({
                  product: product!,
                  priceSystemId: currentPriceSystem?.id,
                  mode: 'percent',
                  ref: 'cost',
                }) + '%'}
              </p>
            </div>
          </div>
          <Tooltip
            place="bottom"
            content="La resolución 209 del 25 de junio de 2024 publicada en 
            la Gaceta Oficial de Cuba y efectiva a partir del 1ero de julio del 
            mismo año, establece que la tasa de utilidad máxima sobre costos y gastos 
            no debe exceder el 30%."
            id="info"
            style={{ maxWidth: '250px' }}
          />
        </div>
      )}
      <div>
        <form
          onSubmit={handleSubmit(onSubmit)}
          className="flex h-full items-start"
        >
          <div className="relative flex w-full flex-col gap-y-5 p-1">
            {!roleIsAdmin && (
              <div className="absolute left-0 z-10 h-full w-full bg-white opacity-25"></div>
            )}

            {!priceSystem && currentPrice && (
              <div className="flex flex-col gap-0">
                <CurrencyInput
                  name="prices.0"
                  label="Precio"
                  control={control}
                  currencies={
                    business?.availableCurrencies.map(
                      (currency) => currency.code
                    ) ?? []
                  }
                  byDefault={{
                    price: currentPrice?.price ?? 0,
                    codeCurrency: currentPrice?.codeCurrency!,
                  }}
                  systemPriceId={currentPrice?.priceSystemId}
                  disabled={product?.onSale}
                />
                {!product?.onSale && (
                  <p className="mt-1 flex gap-2 text-xs text-gray-500">
                    Última modificación:
                    <span className="font-semibold">
                      {formatCalendar(currentPrice?.updatedAt)}
                    </span>
                  </p>
                )}
                {product?.onSale && (
                  <div className="flex items-center gap-5">
                    <span className="text-xs text-gray-600">
                      * Este producto está en oferta
                    </span>
                    <span className="text-xs font-semibold text-gray-600">
                      {getPrice({ product: product! }) as string}
                    </span>
                  </div>
                )}
              </div>
            )}

            {business?.priceSystems && business.priceSystems.length > 1 && (
              <div className="inline-flex items-center gap-3">
                <GenericToggle
                  changeState={setPriceSystem}
                  currentState={priceSystem}
                  title="Habilitar sistema de precios"
                />

                {!!priceSystem && (
                  <ButtonSelector
                    selected={currentPriceSystem}
                    data={
                      business?.priceSystems.map((item) => ({
                        id: item.id,
                        name: item.name,
                      })) ?? []
                    }
                    setSelected={setCurrentPriceSystem}
                  />
                )}
              </div>
            )}

            {priceSystem && (
              <div className="max-h-full overflow-auto scrollbar-none">
                {product?.onSale && (
                  <AlertSquare
                    text={`Los precios no pueden ser modificados mientras el producto se encuentre en oferta`}
                  />
                )}

                {business?.priceSystems.map((item, idx) => (
                  <CurrencyInput
                    key={idx}
                    name={`prices.${idx}`}
                    label={`Precio de venta (${item.name})`}
                    control={control}
                    currencies={
                      business?.availableCurrencies.map(
                        (currency) => currency.code
                      ) ?? []
                    }
                    byDefault={{
                      price:
                        product?.prices.find(
                          (price) => price.priceSystemId === item.id
                        )?.price ?? 0,
                      codeCurrency:
                        product?.prices.find(
                          (price) => price.priceSystemId === item.id
                        )?.codeCurrency ?? 'CUP',
                    }}
                    systemPriceId={item.id}
                    disabled={product?.onSale}
                  />
                ))}
              </div>
            )}
          </div>
          {denyRoles(['AUDITOR']) && (
            <div className="absolute -bottom-[4.5rem] -right-[0.1rem] flex justify-end self-end py-5">
              <Button type="submit" name="Actualizar" color="slate-600" />
            </div>
          )}
        </form>
      </div>
    </div>
  )
}

export default Sales_Price
